import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { LoadingOverlay } from '@mantine/core';
import ConsultantLayout from '@layouts/ConsultantLayout';
import { VENDOR_NAV_ITEMS } from '@constants/sidebar';
import { useCurrentUser } from '@hooks/useCurrentUser';
import LockedModuleGate from '@routes/heimdall/LockedModuleGate';

/**
 * Where a manufacturer login is allowed to go.
 *
 * Derived from the nav rather than restated, so a screen cannot be added to the sidebar and
 * forgotten here — the symptom of which is a link that silently bounces back to Stock.
 * `/vendor/upload` is listed separately: it has left the nav, but the route still answers until
 * the Receive grid absorbs its CSV import.
 */
const VENDOR_ROUTES = [
    ...VENDOR_NAV_ITEMS.filter((item) => item.available).map((item) => item.path),
    '/vendor/upload',
];

export function RoleAwareLayout() {
    const { currentUser, isLoadingUser } = useCurrentUser();
    const location = useLocation();

    if (isLoadingUser) {
        return <LoadingOverlay visible />;
    }

    // A B2B customer's login: the customer portal and nothing else.
    if (currentUser?.role === 'CUSTOMER') {
        if (!location.pathname.startsWith('/portal/')) return <Navigate to="/portal/home" replace />;
        return <Outlet />;
    }

    if (currentUser?.role === 'CONSULTANT') {
        return <ConsultantLayout />;
    }

    if (currentUser?.role === 'VENDOR') {
        const isVendorRoute = VENDOR_ROUTES.some(
            (r) => location.pathname === r || location.pathname.startsWith(`${r}/`),
        );

        if (!isVendorRoute) {
            // Home, not Stock: the mental model on a production floor is "I have a job to
            // do", and opening on a balance sheet asks somebody to translate from what they
            // came here for into what the system happens to store.
            return <Navigate to="/vendor/home" replace />;
        }

        return <Outlet />;
    }

    // The customer portal is for customer logins; brand users have the Sales Orders view of it.
    if (location.pathname.startsWith('/portal/')) return <Navigate to="/home" replace />;

    // Brand users: screens outside the plan are Premium in the sidebar and refused here.
    return (
        <LockedModuleGate>
            <Outlet />
        </LockedModuleGate>
    );
}
